// =============================================
// Prompt Compiler — safe template filling + output validation
// Used by: draftGenerator, writingProfileGenerator, optimization, humanization
// NOT a global replacement for fillTemplate — only for content engine
// =============================================

import { logIntegrity } from "./contentLogger";
import { withDateVars } from "@/lib/dateVars";

/**
 * Fill template variables and THROW if any {{...}} remain unresolved.
 * Use for critical prompts where missing vars = broken output.
 */
export function compilePrompt(
  template: string,
  vars: Record<string, string>,
  step: string,
): string {
  let result = template;
  // current_year/current_date doklejane automatycznie (fix "zly rok 2025
  // w naglowkach"); vars od wywolujacego wygrywaja przy kolizji nazw.
  for (const [key, value] of Object.entries(withDateVars(vars))) {
    result = result.replaceAll(`{{${key}}}`, value ?? "");
  }

  // Check for unresolved variables
  const unresolved = result.match(/\{\{[^}]+\}\}/g);
  if (unresolved && unresolved.length > 0) {
    const uniqueUnresolved = [...new Set(unresolved)];
    logIntegrity("prompt_unresolved_vars", false, {
      step,
      unresolved: uniqueUnresolved,
      provided: Object.keys(vars),
    });
    throw new Error(`[promptCompiler] Step "${step}": unresolved template vars: ${uniqueUnresolved.join(", ")}. Provided: ${Object.keys(vars).join(", ")}`);
  }

  return result;
}

/**
 * Fill template variables. If unresolved vars remain, return fallback instead of throwing.
 * Use for intro and other prompts where fallback is acceptable.
 */
export function compilePromptSafe(
  template: string,
  vars: Record<string, string>,
  step: string,
  fallback: string,
): string {
  try {
    return compilePrompt(template, vars, step);
  } catch {
    logIntegrity("prompt_fallback_used", false, { step, reason: "unresolved vars in DB template" });
    return fallback;
  }
}

/**
 * Validate that LLM output doesn't contain meta/template artifacts.
 * Returns { passed, found } — found contains the problematic strings.
 */
const META_PATTERNS = [
  "{{",
  "}}",
  "brakuje danych",
  "podeślij",
  "podeslij",
  "uzupełnij",
  "uzupelnij",
  "na ten moment",
  "gdy dostanę",
  "gdy dostane",
  "nie podano",
  "wklej je proszę",
  "wklej je prosze",
  "## Nagłówek",
  "## Naglowek",
  "## Typ sekcji",
  "## Dane z Knowledge Graph",
  "Output Format",
  "Podsekcje H3",
  "## ZASADY",
  "## Format pisania",
  "## Kontekst poprzednich sekcji",
];

export function validateNoMetaOutput(
  html: string,
  stage: string,
): { passed: boolean; found?: string[] } {
  const htmlLower = html.toLowerCase();
  const found = META_PATTERNS.filter((p) => htmlLower.includes(p.toLowerCase()));

  if (found.length > 0) {
    logIntegrity("meta_output_check", false, { stage, found });
    return { passed: false, found };
  }

  logIntegrity("meta_output_check", true, { stage });
  return { passed: true };
}
